/**
 * NotificationHelpDialog - In-app help for setting up notifications
 * Shows quick setup instructions with platform-specific guides
 */

import { useState } from 'react';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { 
  HelpCircle, 
  Slack, 
  ExternalLink, 
  Copy,
  CheckCircle,
  Info,
  Clock,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

interface NotificationHelpDialogProps {
  context?: 'engagement' | 'personal';
}

// MS Teams icon
const TeamsIcon = () => (
  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.19 8.77a3.58 3.58 0 0 0-3.29-3.22h-4.36a.58.58 0 0 0-.58.58v7.78a.58.58 0 0 0 .58.58h4.36a3.58 3.58 0 0 0 3.29-3.22v-2.5zm-8.83-2.64H5.64a3 3 0 0 0-3 3v5.44a3 3 0 0 0 3 3h4.72a.58.58 0 0 0 .58-.58V6.71a.58.58 0 0 0-.58-.58z"/>
  </svg>
);

export function NotificationHelpDialog({ context = 'personal' }: NotificationHelpDialogProps) {
  const [open, setOpen] = useState(false);
  const [copiedSlack, setCopiedSlack] = useState(false);
  const [copiedTeams, setCopiedTeams] = useState(false);

  const copySlackUrl = () => {
    navigator.clipboard.writeText('https://hooks.slack.com/services/YOUR/WEBHOOK/URL');
    setCopiedSlack(true);
    setTimeout(() => setCopiedSlack(false), 2000);
    toast.success('Example URL copied');
  };

  const copyTeamsUrl = () => {
    navigator.clipboard.writeText('https://outlook.office.com/webhook/YOUR/WEBHOOK/URL');
    setCopiedTeams(true);
    setTimeout(() => setCopiedTeams(false), 2000);
    toast.success('Example URL copied');
  };

  const isEngagement = context === 'engagement';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <HelpCircle className="size-4 mr-2" />
          Setup Help
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="size-5" />
            {isEngagement ? 'Engagement Notifications Setup' : 'Personal Notifications Setup'}
          </DialogTitle>
          <DialogDescription>
            {isEngagement 
              ? 'Set up Slack or Teams notifications for your entire engagement team'
              : 'Set up your personal notification fallback for when engagements don\'t have channels'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* How It Works */}
          <Alert>
            <Info className="size-4" />
            <AlertDescription>
              <strong>How notifications work:</strong> PathKit uses a three-tier system.
              {isEngagement ? (
                <span> Engagement notifications go to your team channel first. If not set up, they fall back to personal channels, then email.</span>
              ) : (
                <span> Your personal integration is used when engagements don't have their own channels. Email is always the final fallback.</span>
              )}
            </AlertDescription>
          </Alert>

          {/* What You'll Get */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Clock className="size-5 text-blue-600 mt-0.5" />
              <div className="space-y-2">
                <p className="font-medium text-blue-900">Daily Reminders (8:00 AM & 9:00 AM ET)</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>8am:</strong> Action items due tomorrow</li>
                  <li>• <strong>9am:</strong> Overdue action items</li>
                  <li>• Grouped by {isEngagement ? 'engagement' : 'client'}</li>
                  <li>• Includes item name, due date, and status</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Platform Instructions */}
          <Tabs defaultValue="slack">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="slack" className="flex items-center gap-2">
                <Slack className="size-4" />
                Slack Setup
              </TabsTrigger>
              <TabsTrigger value="teams" className="flex items-center gap-2">
                <TeamsIcon />
                Teams Setup
              </TabsTrigger>
            </TabsList>

            {/* Slack Instructions */}
            <TabsContent value="slack" className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 1</Badge>
                  Open Your Slack Workspace
                </h4>
                <p className="text-sm text-muted-foreground">
                  Go to the {isEngagement ? 'engagement channel (e.g., #acme-implementation)' : 'channel where you want to receive notifications (or create a new one like #my-notifications)'}
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 2</Badge>
                  Add Incoming Webhooks
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Click the <strong>channel name</strong> at the top</li>
                  <li>Go to <strong>Integrations</strong> tab</li>
                  <li>Click <strong>Add an app</strong></li>
                  <li>Search for "Incoming Webhooks"</li>
                  <li>Click <strong>Add</strong></li>
                </ol>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 3</Badge>
                  Copy the Webhook URL
                </h4>
                <div className="bg-gray-50 border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2">Your webhook URL will look like this:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-x-auto">
                      https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXX
                    </code>
                    <Button size="sm" variant="outline" onClick={copySlackUrl}>
                      {copiedSlack ? <CheckCircle className="size-4 text-green-600" /> : <Copy className="size-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 4</Badge>
                  Paste in PathKit
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Paste the webhook URL in the input above</li>
                  <li>Enter the channel name (optional, e.g., <code>#acme-implementation</code>)</li>
                  <li>Click <strong>Save Integration</strong></li>
                  <li>Click <strong>Send Test Message</strong> to verify</li>
                </ol>
              </div>

              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="size-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Setup complete.</strong> You will receive notifications at 8am and 9am ET daily.
                </AlertDescription>
              </Alert>

              <a
                href="https://api.slack.com/messaging/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                View official Slack documentation <ExternalLink className="size-3" />
              </a>
            </TabsContent>

            {/* Teams Instructions */}
            <TabsContent value="teams" className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 1</Badge>
                  Open Microsoft Teams
                </h4>
                <p className="text-sm text-muted-foreground">
                  Go to the {isEngagement ? 'engagement channel' : 'channel where you want to receive notifications'}
                </p>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 2</Badge>
                  Add Incoming Webhook Connector
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Click the <strong>⋯ menu</strong> (three dots) next to the channel name</li>
                  <li>Select <strong>Connectors</strong></li>
                  <li>Search for "Incoming Webhook"</li>
                  <li>Click <strong>Add</strong> or <strong>Configure</strong></li>
                </ol>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 3</Badge>
                  Configure the Webhook
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Name it: <strong>"PathKit Notifications"</strong></li>
                  <li>Optionally upload a logo/icon</li>
                  <li>Click <strong>Create</strong></li>
                </ol>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 4</Badge>
                  Copy the Webhook URL
                </h4>
                <div className="bg-gray-50 border rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-2">Your webhook URL will look like this:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-white px-2 py-1 rounded border flex-1 overflow-x-auto">
                      https://outlook.office.com/webhook/YOUR-WEBHOOK-URL
                    </code>
                    <Button size="sm" variant="outline" onClick={copyTeamsUrl}>
                      {copiedTeams ? <CheckCircle className="size-4 text-green-600" /> : <Copy className="size-4" />}
                    </Button>
                  </div>
                  <Alert className="mt-3 bg-yellow-50 border-yellow-200">
                    <Info className="size-4 text-yellow-800" />
                    <AlertDescription className="text-yellow-800 text-xs">
                      <strong>Important:</strong> Copy this URL immediately! Teams won't show it again.
                    </AlertDescription>
                  </Alert>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Badge>Step 5</Badge>
                  Paste in PathKit
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>Paste the webhook URL in the input above</li>
                  <li>Enter the channel name (optional, e.g., "Acme Implementation")</li>
                  <li>Click <strong>Save Integration</strong></li>
                  <li>Click <strong>Send Test Message</strong> to verify</li>
                </ol>
              </div>

              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="size-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Setup complete.</strong> You will receive notifications at 8am and 9am ET daily.
                </AlertDescription>
              </Alert>

              <a
                href="https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/how-to/add-incoming-webhook"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
              >
                View official Teams documentation <ExternalLink className="size-3" />
              </a>
            </TabsContent>
          </Tabs>

          {/* Troubleshooting */}
          <div className="border-t pt-4">
            <h4 className="font-medium mb-3">Troubleshooting</h4>
            <div className="space-y-2 text-sm">
              <details className="group">
                <summary className="cursor-pointer font-medium text-muted-foreground hover:text-foreground">
                  Test message not showing up?
                </summary>
                <div className="mt-2 pl-4 space-y-1 text-muted-foreground">
                  <p>• Verify the webhook URL is correct (starts with <code>https://hooks.slack.com</code> or <code>https://outlook.office.com</code>)</p>
                  <p>• Check you added the webhook to the correct channel</p>
                  <p>• Try removing and re-adding the webhook integration</p>
                </div>
              </details>
              
              <details className="group">
                <summary className="cursor-pointer font-medium text-muted-foreground hover:text-foreground">
                  Not receiving daily reminders?
                </summary>
                <div className="mt-2 pl-4 space-y-1 text-muted-foreground">
                  <p>• Make sure you have action items with due dates assigned to you</p>
                  <p>• Check that items are not marked as "Completed"</p>
                  <p>• Verify the test message works (if test works, reminders will too)</p>
                </div>
              </details>
            </div>
          </div>

          {/* Close button */}
          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setOpen(false)}>
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}